import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Pencil, X, Plus, Trash2, Phone, Mail, Star, UserRound, MessageCircle, CreditCard, ImagePlus, ImageOff, FileText, UploadCloud, FileSpreadsheet, Download, FlaskConical, ChevronRight, MapPin, AlertTriangle, Clock, Home, Building2, Utensils } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import {
  getDiagnosticLabDetail, editDiagnosticLab, geocodeLabAddress, uploadLabPhoto, deleteLabPhoto, uploadLabDocument, deleteLabDocument,
  uploadLabTestSheet, downloadLabTestTemplate, getLabTests, getLabOperations, updateLabTest,
  LAB_TYPE_OPTIONS, LAB_TYPES_REQUIRING_MODALITIES, CONTACT_TYPE_OPTIONS,
  CONTACT_TYPES_WITH_PAN, CONTACT_TYPES_WITH_WHATSAPP, LAB_PHOTO_SLOTS, LAB_GALLERY_MAX,
  LAB_DOC_TYPES, LAB_DOC_KYC_MAX, LAB_DOC_ACCEPT, LAB_TEST_SHEET_ACCEPT,
  SPECIALIZATION_OPTIONS, MODALITY_OPTIONS, LANGUAGE_OPTIONS, labelFor, formatDateTime, auditName, AUDIT_UNKNOWN,
  validateEstablishedYear, validatePlace, validateEmail, validateMobile, validatePan, validateIfsc,
} from "../services/diagnosticLabsService";
import {
  SegmentedControl, ChipMultiSelect, AsyncChainSelect, StatusBadge, PincodeAutoAddress, LocationPicker, Toast, getCurrentRole,
} from "../components/diagnostic/primitives";
import LabPincodePanel from "../components/diagnostic/LabPincodePanel";
import TestsDrawer from "../components/diagnostic/TestsDrawer";
import OperationsModal from "../components/diagnostic/OperationsModal";
import { operationsSummary } from "../components/diagnostic/OperationsForm";
import { useBlurFieldErrors } from "../components/diagnostic/useBlurFieldErrors";

const inputCls =
  "w-full px-3 py-2 border border-[#D6DFEA] rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#00A6A6]/60 focus:border-[#00A6A6] transition-colors bg-[#FAFCFE] disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-700 disabled:cursor-default";

const SectionCard = ({ title, subtitle, children, allowOverflow = false }) => (
  <div className={`bg-white rounded-2xl border border-[#E4EBF3] shadow-[0_1px_2px_rgba(15,76,129,0.04)] hover:shadow-[0_8px_24px_-10px_rgba(15,76,129,0.18)] transition-shadow duration-300 ${allowOverflow ? "" : "overflow-hidden"}`}>
    <div className="px-6 py-4 border-b border-[#EEF3F8] bg-gradient-to-r from-[#F7FAFD] to-transparent">
      <h3 className="text-sm font-semibold text-[#0F4C81] flex items-center gap-2 before:content-[''] before:w-1 before:h-4 before:rounded-full before:bg-gradient-to-b before:from-[#0F4C81] before:to-[#00A6A6]">{title}</h3>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5 pl-3">{subtitle}</p>}
    </div>
    <div className="px-6 py-5">{children}</div>
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

// Who did it, under when it happened. `by` comes from the backend's history
// tables, so it is absent on older payloads and on labs that predate history
// tracking — in both cases the line is dropped rather than rendered as
// "by undefined".
const AuditRow = ({ label, at, by }) => (
  <div className="flex justify-between gap-3">
    <dt className="text-gray-400 shrink-0">{label}</dt>
    <dd className="text-gray-600 text-right">
      {formatDateTime(at)}
      {by?.name && by.name !== AUDIT_UNKNOWN && (
        <span className="block text-xs text-gray-400 truncate max-w-[160px]" title={by.email || by.name}>
          by {auditName(by)}
        </span>
      )}
    </dd>
  </div>
);

const LAB_TABS = [
  { key: "overview", label: "Overview", icon: Building2 },
  { key: "contacts", label: "Contacts", icon: UserRound },
  { key: "media", label: "Media", icon: ImagePlus },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "bank", label: "Bank", icon: CreditCard },
  { key: "tests", label: "Tests", icon: FlaskConical },
  { key: "operations", label: "Operations", icon: Clock },
];

// Horizontal tab bar with an animated sliding pill (framer-motion layoutId).
const LabTabBar = ({ active, onChange }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-1.5 flex gap-1 overflow-x-auto">
    {LAB_TABS.map((t) => {
      const Icon = t.icon;
      const sel = active === t.key;
      return (
        <button key={t.key} type="button" onClick={() => onChange(t.key)}
          className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${sel ? "text-[#0F4C81]" : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"}`}>
          {sel && (
            <motion.span layoutId="labTabPill"
              className="absolute inset-0 rounded-xl bg-[#0F4C81]/[0.07] ring-1 ring-inset ring-[#0F4C81]/20"
              transition={{ type: "spring", stiffness: 400, damping: 32 }} />
          )}
          <Icon className="w-4 h-4 relative z-10" />
          <span className="relative z-10">{t.label}</span>
        </button>
      );
    })}
  </div>
);

// Fade/slide-in used when switching tab panels.
// Opacity-only (no transform): a CSS transform on an ancestor breaks Google Maps
// pin/drag coordinate math, and the LocationPicker lives inside the Overview panel.
const panelMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2, ease: "easeOut" },
};

const DiagnosticLabDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The list passes its own URL (page + filters) so Back returns to the same page.
  const backTo = location.state?.from || "/subscriber-wallet/labs/labs";
  const { labId } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const currentRole = getCurrentRole();
  const canAccess = currentRole === "Ops" || currentRole === "SuperAdmin";

  const [lab, setLab] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(null);
  const [docBusy, setDocBusy] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [testProcessing, setTestProcessing] = useState(false);
  const [testsDrawerOpen, setTestsDrawerOpen] = useState(false);
  const [pendingTestFile, setPendingTestFile] = useState(null);

  // Operations & schedule — own load/edit/save lifecycle (separate API).
  const [operations, setOperations] = useState(null); // raw API data; null = not configured
  const [opsLoading, setOpsLoading] = useState(true);
  const [opsDrawerOpen, setOpsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const blur = useBlurFieldErrors();
  const ck = (i, field) => `contact_${i}_${field}`;

  const ro = !editing; // read-only
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setBank = (key, value) => setForm((f) => ({ ...f, bank: { ...f.bank, [key]: value } }));

  const emptyContact = { type: "", name: "", mobile: "", email: "", pan: "", whatsapp: "", is_primary: false };
  const addContact = () => setForm((f) => ({ ...f, contacts: [...(f.contacts || []), { ...emptyContact }] }));
  const removeContact = (idx) => setForm((f) => ({ ...f, contacts: f.contacts.filter((_, i) => i !== idx) }));
  const updateContact = (idx, key, value) =>
    setForm((f) => ({
      ...f,
      contacts: f.contacts.map((c, i) => {
        if (i !== idx) return key === "is_primary" && value ? { ...c, is_primary: false } : c;
        return { ...c, [key]: value };
      }),
    }));
  const needsModalities = form && LAB_TYPES_REQUIRING_MODALITIES.includes(form.lab_type);
  const descLen = form?.description?.length || 0;
  const tagLen = form?.tagline?.length || 0;
  const yearErr = validateEstablishedYear(form?.established_year || "");
  const ifscErr = validateIfsc(form?.bank?.ifsc_code || "");
  const cityErr = validatePlace(form?.city || "", "City");
  const stateErr = validatePlace(form?.state || "", "State");
  const brandErr = !form?.brand_name?.trim() ? "Brand name is required." : "";
  const businessErr = form?.business_name?.trim().length < 3 ? "Legal name must be at least 3 characters." : "";
  const labTypeErr = !form?.lab_type ? "Select a lab type." : "";
  const languagesErr = form?.languages?.length === 0 ? "Select at least one language." : "";
  const modalitiesErr = needsModalities && form?.modalities?.length === 0 ? "Radiology / multi-modality labs need at least one modality." : "";
  const descFieldErr = descLen > 500 ? "Description must be 500 characters or fewer." : "";
  const tagFieldErr = tagLen > 80 ? "Tagline must be 80 characters or fewer." : "";
  const latFieldErr = form?.latitude && (Number.isNaN(Number(form.latitude)) || Math.abs(Number(form.latitude)) > 90)
    ? "Latitude must be a number between -90 and 90." : "";
  const lngFieldErr = form?.longitude && (Number.isNaN(Number(form.longitude)) || Math.abs(Number(form.longitude)) > 180)
    ? "Longitude must be a number between -180 and 180." : "";

  const contactFieldErrors = (i, c) => {
    const label = `Contact ${i + 1}`;
    const hasData = c.name || c.mobile || c.email || c.pan || c.whatsapp || c.type;
    if (!hasData) return {};
    const e = {};
    if (!c.name?.trim()) e[ck(i, "name")] = `${label}: name is required.`;
    if (!c.mobile?.trim()) e[ck(i, "mobile")] = `${label}: mobile is required.`;
    else if (validateMobile(c.mobile)) e[ck(i, "mobile")] = `${label}: ${validateMobile(c.mobile)}`;
    if (validateEmail(c.email)) e[ck(i, "email")] = `${label}: ${validateEmail(c.email)}`;
    if (validatePan(c.pan)) e[ck(i, "pan")] = `${label}: ${validatePan(c.pan)}`;
    if (validateMobile(c.whatsapp, "WhatsApp")) e[ck(i, "whatsapp")] = `${label}: ${validateMobile(c.whatsapp, "WhatsApp")}`;
    return e;
  };
  const allContactErrors = () => (form?.contacts || []).reduce((acc, c, i) => ({ ...acc, ...contactFieldErrors(i, c) }), {});

  const overviewBlurKeys = () => {
    const keys = ["brand_name", "business_name", "lab_type", "languages", "established_year", "tagline", "description", "city", "state", "latitude", "longitude"];
    if (needsModalities) keys.push("modalities");
    return keys;
  };

  // First contact-level message. Name + mobile are mandatory for any contact
  // that has data; fully-empty rows are dropped on save and don't block.
  const toForm = (data) => ({
    business_name: data.business_name || "",
    brand_name: data.brand_name || "",
    lab_type: data.lab_type || "",
    established_year: data.established_year || "",
    total_booking: data.total_booking || "",
    specializations: data.specializations || [],
    modalities: data.modalities || [],
    languages: data.languages || [],
    tagline: data.tagline || "",
    description: data.description || "",
    type: data.type || "offline",
    service_model: data.service_model || "local",
    active: data.active !== false,
    chain: data.chain?.id || null,
    chain_label: data.chain?.brand_name || "",
    address_line: data.address_line || "",
    landmark: data.landmark || "",
    locality: data.locality || "",
    city: data.city || "",
    state: data.state || "",
    pincode: data.pincode || "",
    latitude: data.latitude || "",
    longitude: data.longitude || "",
    contacts: (data.contacts || []).map((c) => ({
      type: c.type || "", name: c.name || "", mobile: c.mobile || "",
      email: c.email || "", pan: c.pan || "", whatsapp: c.whatsapp || "",
      is_primary: !!c.is_primary,
    })),
    bank: {
      account_name: data.bank?.account_name || "",
      account_number: data.bank?.account_number || "",
      ifsc_code: data.bank?.ifsc_code || "",
      bank_name: data.bank?.bank_name || "",
      branch_name: data.bank?.branch_name || "",
      account_type: data.bank?.account_type || "",
    },
  });

  const hydrate = (data) => {
    setLab(data);
    setForm(toForm(data));
  };

  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAccess) return;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await getDiagnosticLabDetail(currentRole, labId);
        hydrate(resp?.msg || resp);
      } catch (err) {
        setError(err.message || "Failed to load lab.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canAccess, labId]);

  // Load the operations config independently of the main lab record.
  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAccess) return;
    const loadOps = async () => {
      setOpsLoading(true);
      try {
        const data = await getLabOperations(currentRole, labId);
        setOperations(data);
      } catch {
        setOperations(null);
      } finally {
        setOpsLoading(false);
      }
    };
    loadOps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canAccess, labId]);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3200); };

  // The drawer owns validation + the save call; here we just absorb the result.
  const handleOpsSaved = (data) => {
    setOperations(data);
    showToast("success", "Operations saved.");
  };

  const opsSummary = operationsSummary(operations); // null when not configured
  const inr = (val) => `₹${Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  const cancelEdit = () => {
    setForm(toForm(lab));
    setEditing(false);
    blur.reset();
  };

  // Surface a validation failure: jump to the tab that holds the field so the
  // user actually sees what's wrong (fields on hidden tabs are still in state).
  const failOn = (tab, message) => { setActiveTab(tab); showToast("error", message); };

  const handleSave = async () => {
    const contactErrs = allContactErrors();
    blur.touchMany([...overviewBlurKeys(), "ifsc_code", ...Object.keys(contactErrs)]);

    if (!form.brand_name.trim()) return failOn("overview", "Brand name is required.");
    if (form.business_name.trim().length < 3) return failOn("overview", "Legal name must be at least 3 characters.");
    if (!form.lab_type) return failOn("overview", "Select a lab type.");
    if (form.languages.length === 0) return failOn("overview", "Select at least one language.");
    if (needsModalities && form.modalities.length === 0) return failOn("overview", "Radiology / multi-modality labs need at least one modality.");
    if (descLen > 500) return failOn("overview", "Description must be 500 characters or fewer.");
    // National labs have no single address — only the year matters here for them.
    const isNational = form.service_model === "national";
    if (yearErr) return failOn("overview", yearErr);
    if (!isNational) {
      if (cityErr || stateErr) return failOn("overview", cityErr || stateErr);
      if (latFieldErr) return failOn("overview", latFieldErr);
      if (lngFieldErr) return failOn("overview", lngFieldErr);
    }
    if (ifscErr) return failOn("bank", ifscErr);
    const firstContactErr = Object.values(contactErrs)[0];
    if (firstContactErr) return failOn("contacts", firstContactErr);

    setSaving(true);
    try {
      const payload = {
        business_name: form.business_name.trim(),
        brand_name: form.brand_name.trim(),
        lab_type: form.lab_type,
        established_year: form.established_year?.trim() || null,
        total_booking: form.total_booking ? Number(form.total_booking) : null,
        specializations: form.specializations,
        modalities: form.modalities,
        languages: form.languages,
        tagline: form.tagline.trim(),
        description: form.description.trim(),
        type: form.type,
        service_model: form.service_model,
        active: form.active,
        chain: form.chain || null,
        // National labs serve by uploaded pincodes, not a single address.
        ...(form.service_model === "national"
          ? {
              address_line: "", landmark: "", locality: "", city: "", state: "", pincode: "",
              latitude: null, longitude: null,
            }
          : {
              address_line: form.address_line.trim(),
              landmark: form.landmark.trim(),
              locality: form.locality.trim(),
              city: form.city.trim(),
              state: form.state.trim(),
              pincode: form.pincode.trim(),
              latitude: form.latitude !== "" && form.latitude != null ? Number(form.latitude) : null,
              longitude: form.longitude !== "" && form.longitude != null ? Number(form.longitude) : null,
            }),
        contacts: (form.contacts || [])
          .map((c) => ({
            type: c.type || null,
            name: (c.name || "").trim(),
            mobile: (c.mobile || "").trim(),
            email: (c.email || "").trim(),
            pan: (c.pan || "").trim(),
            whatsapp: (c.whatsapp || "").trim(),
            is_primary: !!c.is_primary,
          }))
          .filter((c) => c.name || c.mobile || c.email || c.pan || c.whatsapp),
        bank: {
          account_name: form.bank.account_name.trim(),
          account_number: form.bank.account_number.trim(),
          ifsc_code: form.bank.ifsc_code.trim().toUpperCase(),
          bank_name: form.bank.bank_name.trim(),
          branch_name: form.bank.branch_name.trim(),
          account_type: form.bank.account_type || null,
        },
      };
      const resp = await editDiagnosticLab(currentRole, labId, payload);
      const data = resp?.msg || resp;
      // Keep the coordinates the user saved — the API may echo back geocoded values.
      if (data && payload.latitude != null && payload.longitude != null) {
        data.latitude = payload.latitude;
        data.longitude = payload.longitude;
      }
      hydrate(data);
      setEditing(false);
      blur.reset();
      showToast("success", "Lab saved successfully.");
    } catch (err) {
      showToast("error", err.message || "Failed to save lab.");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (photoType, file, busyKey) => {
    if (!file) return;
    setPhotoBusy(busyKey || photoType);
    try {
      const resp = await uploadLabPhoto(currentRole, labId, photoType, file);
      const photos = (resp?.msg || resp)?.photos;
      if (photos) setLab((l) => ({ ...l, photos }));
      showToast("success", "Photo uploaded.");
    } catch (err) {
      showToast("error", err.message || "Upload failed.");
    } finally {
      setPhotoBusy(null);
    }
  };

  const handlePhotoDelete = async (documentId) => {
    setPhotoBusy(documentId);
    try {
      const resp = await deleteLabPhoto(currentRole, labId, documentId);
      const photos = (resp?.msg || resp)?.photos;
      if (photos) setLab((l) => ({ ...l, photos }));
      showToast("success", "Photo removed.");
    } catch (err) {
      showToast("error", err.message || "Remove failed.");
    } finally {
      setPhotoBusy(null);
    }
  };

  const handleDocUpload = async (docType, file) => {
    if (!file) return;
    setDocBusy(docType);
    try {
      const resp = await uploadLabDocument(currentRole, labId, docType, file);
      const documents = (resp?.msg || resp)?.documents;
      if (documents) setLab((l) => ({ ...l, documents }));
      showToast("success", "Document uploaded.");
    } catch (err) {
      showToast("error", err.message || "Upload failed.");
    } finally {
      setDocBusy(null);
    }
  };

  const handleDocDelete = async (documentId) => {
    setDocBusy(documentId);
    try {
      const resp = await deleteLabDocument(currentRole, labId, documentId);
      const documents = (resp?.msg || resp)?.documents;
      if (documents) setLab((l) => ({ ...l, documents }));
      showToast("success", "Document removed.");
    } catch (err) {
      showToast("error", err.message || "Remove failed.");
    } finally {
      setDocBusy(null);
    }
  };

  // A re-upload replaces the current menu, so confirm first when a sheet exists.
  const pickTestFile = (file) => {
    if (!file) return;
    if (testSheet.current) setPendingTestFile(file);
    else handleTestSheetUpload(file);
  };

  const confirmTestUpload = () => {
    const file = pendingTestFile;
    setPendingTestFile(null);
    if (file) handleTestSheetUpload(file);
  };

  const handleTestSheetUpload = async (file) => {
    if (!file) return;
    setTestBusy(true);
    try {
      const resp = await uploadLabTestSheet(currentRole, labId, file);
      const test_sheet = (resp?.msg || resp)?.test_sheet;
      if (test_sheet) setLab((l) => ({ ...l, test_sheet }));
      showToast("success", "Sheet uploaded — parsing in the background…");
      // Parsing runs in a Celery task; poll a few times until the rows land,
      // then reflect the count on the card (without resetting the edit form).
      setTestProcessing(true);
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      let found = 0;
      for (let i = 0; i < 8; i += 1) {
        await sleep(2000);
        try {
          const r = await getLabTests(currentRole, labId, { items: 1 });
          found = (r?.msg || r)?.pagination?.total || 0;
          if (found > 0) break;
        } catch { /* keep polling */ }
      }
      // The parse task produces the imported count AND (if any rows failed) an
      // error report asynchronously. Refetch the test_sheet to land both; merge
      // only test_sheet so the edit form is left untouched. Fall back to a
      // count-only patch if the detail refetch fails.
      let errorFile = null;
      try {
        const detail = await getDiagnosticLabDetail(currentRole, labId);
        const fresh = (detail?.msg || detail)?.test_sheet;
        if (fresh) {
          setLab((l) => ({ ...l, test_sheet: fresh }));
          errorFile = fresh.current?.error_file || null;
          if (typeof fresh.current?.test_count === "number") found = fresh.current.test_count;
        }
      } catch {
        if (found > 0) {
          setLab((l) => (l?.test_sheet?.current
            ? { ...l, test_sheet: { ...l.test_sheet, current: { ...l.test_sheet.current, test_count: found } } }
            : l));
        }
      }
      setTestProcessing(false);
      if (errorFile) {
        showToast("info", `${found} test${found === 1 ? "" : "s"} imported — some rows had errors. Download the error report to fix them.`);
      } else {
        showToast(found ? "success" : "info", found ? `${found} tests imported.` : "Still processing — refresh in a moment.");
      }
    } catch (err) {
      setTestProcessing(false);
      showToast("error", err.message || "Upload failed.");
    } finally {
      setTestBusy(false);
    }
  };

  const handleTemplateDownload = async () => {
    try {
      await downloadLabTestTemplate(currentRole);
    } catch (err) {
      showToast("error", err.message || "Download failed.");
    }
  };

  const handleGeocode = async () => {
    setGeoBusy(true);
    try {
      const resp = await geocodeLabAddress(currentRole, {
        address_line: form.address_line, locality: form.locality, landmark: form.landmark,
        city: form.city, state: form.state, pincode: form.pincode,
      });
      const d = resp?.msg || resp;
      if (d?.latitude && d?.longitude) {
        setForm((f) => ({ ...f, latitude: d.latitude, longitude: d.longitude }));
        showToast("success", "Coordinates fetched.");
      } else {
        showToast("error", "Couldn't fetch coordinates for this address.");
      }
    } catch (err) {
      showToast("error", err.message || "Geocoding failed.");
    } finally {
      setGeoBusy(false);
    }
  };

  const photos = lab?.photos || {};
  const gallery = photos.gallery || [];
  const documents = lab?.documents || {};
  const testSheet = lab?.test_sheet || { current: null, versions: [] };
  // contract: loan-and-subscription/internalapi/views/user_type/shared/diagnostic/lab/serializers.py _test_source_payload
  const chainManaged = lab?.test_source?.type === "chain" ? lab.test_source : null;
  const liveTestCount = chainManaged ? chainManaged.test_count || 0 : testSheet.current?.test_count || 0;

  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64 text-center">
        <div><h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to access this page.</p></div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F9FD] to-[#E9F1F8] p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-5xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(backTo)}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all shrink-0" aria-label="Back">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900 truncate">{loading ? "Loading…" : lab?.brand_name || "Lab"}</h1>
                {lab && <StatusBadge status={lab.active === false ? "inactive" : "active"} />}
              </div>
              <p className="text-sm text-gray-400">{lab?.chain?.brand_name ? `Member of ${lab.chain.brand_name}` : "Standalone lab"}</p>
            </div>
          </div>
          {!loading && form && activeTab !== "operations" && (
            editing ? (
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={cancelEdit} disabled={saving}
                  className="px-4 py-2.5 text-sm text-gray-700 border border-gray-300 rounded-xl hover:bg-white disabled:opacity-50 flex items-center gap-2 transition-all">
                  <X className="w-4 h-4" /> Cancel
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2.5 bg-[#0F4C81] text-white rounded-xl text-sm font-medium hover:bg-[#0C3E6B] shadow-sm disabled:opacity-50 flex items-center gap-2 transition-all">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            ) : (
              <button onClick={() => { setEditing(true); blur.reset(); }}
                className="px-4 py-2.5 bg-[#0F4C81] text-white rounded-xl text-sm font-medium hover:bg-[#0C3E6B] shadow-sm flex items-center gap-2 transition-all shrink-0">
                <Pencil className="w-4 h-4" /> Edit
              </button>
            )
          )}
        </div>

        {error && <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-700">{error}</div>}

        {loading || !form ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-16 flex flex-col items-center gap-3 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" /><span className="text-sm">Loading lab…</span>
          </div>
        ) : (
          <>
            <LabTabBar active={activeTab} onChange={setActiveTab} />

            {activeTab === "overview" && (
              <motion.div key="overview" {...panelMotion} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <SectionCard title="Identity">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Brand name" required>
                        <input className={blur.errCls("brand_name", brandErr, inputCls)} disabled={ro} value={form.brand_name}
                          onChange={(e) => set("brand_name", e.target.value)} onBlur={() => blur.touch("brand_name")} />
                        {blur.msg("brand_name", brandErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("brand_name", brandErr)}</p>}
                      </Field>
                      <Field label="Legal entity name" required>
                        <input className={blur.errCls("business_name", businessErr, inputCls)} disabled={ro} value={form.business_name}
                          onChange={(e) => set("business_name", e.target.value)} onBlur={() => blur.touch("business_name")} />
                        {blur.msg("business_name", businessErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("business_name", businessErr)}</p>}
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Lab type" required>
                          <div onBlur={() => blur.touch("lab_type")} tabIndex={-1}>
                            <SegmentedControl disabled={ro} value={form.lab_type} onChange={(v) => set("lab_type", v)} options={LAB_TYPE_OPTIONS} />
                          </div>
                          {blur.msg("lab_type", labTypeErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("lab_type", labTypeErr)}</p>}
                        </Field>
                      </div>
                      <Field label="Year established">
                        <input type="text" inputMode="numeric" maxLength={4}
                          className={blur.errCls("established_year", yearErr, inputCls)}
                          disabled={ro} value={form.established_year}
                          onChange={(e) => set("established_year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                          onBlur={() => blur.touch("established_year")} />
                        {blur.msg("established_year", yearErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("established_year", yearErr)}</p>}
                      </Field>
                      <Field label="Collection modes">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                          {opsLoading ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                            </span>
                          ) : opsSummary ? (
                            <div className="space-y-2">
                              {[
                                { icon: Home, label: "Home collection", on: opsSummary.home },
                                { icon: Building2, label: "Walk-in", on: opsSummary.walkin },
                              ].map(({ icon: Icon, label, on }) => (
                                <div key={label} className="flex items-center justify-between gap-3">
                                  <span className={`inline-flex items-center gap-2 text-sm ${on ? "text-gray-800" : "text-gray-400"}`}>
                                    <Icon className="w-4 h-4 text-gray-400" /> {label}
                                  </span>
                                  <span className="relative inline-flex shrink-0" aria-hidden="true">
                                    <span className={`block w-9 h-5 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-gray-300"}`} />
                                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${on ? "translate-x-4" : "translate-x-0"}`} />
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400">
                              Not configured — add it from the <span className="font-medium text-gray-600">Operations</span> tab.
                            </p>
                          )}
                        </div>
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Specializations">
                          <ChipMultiSelect disabled={ro} options={SPECIALIZATION_OPTIONS} value={form.specializations} onChange={(v) => set("specializations", v)} />
                        </Field>
                      </div>
                      <div className="sm:col-span-2">
                        <Field label="Modalities offered" required={needsModalities}>
                          <div onBlur={() => blur.touch("modalities")} tabIndex={-1}>
                            <ChipMultiSelect disabled={ro} options={MODALITY_OPTIONS} value={form.modalities} onChange={(v) => set("modalities", v)} />
                          </div>
                          {blur.msg("modalities", modalitiesErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("modalities", modalitiesErr)}</p>}
                        </Field>
                      </div>
                      <div className="sm:col-span-2">
                        <Field label="Languages spoken" required>
                          <div onBlur={() => blur.touch("languages")} tabIndex={-1}>
                            <ChipMultiSelect disabled={ro} options={LANGUAGE_OPTIONS} value={form.languages} onChange={(v) => set("languages", v)} searchable creatable commonCount={5} />
                          </div>
                          {blur.msg("languages", languagesErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("languages", languagesErr)}</p>}
                        </Field>
                      </div>
                      <div className="sm:col-span-2">
                        <Field label="Tagline">
                          <input className={blur.errCls("tagline", tagFieldErr, inputCls)} disabled={ro} value={form.tagline} maxLength={80}
                            onChange={(e) => set("tagline", e.target.value)} onBlur={() => blur.touch("tagline")} />
                          {editing && (
                            <p className={`mt-1 text-xs ${blur.msg("tagline", tagFieldErr) ? "text-red-500" : tagLen > 80 ? "text-red-500" : "text-gray-400"}`}>
                              {blur.msg("tagline", tagFieldErr) || `${tagLen}/80`}
                            </p>
                          )}
                        </Field>
                      </div>
                      <div className="sm:col-span-2">
                        <Field label="Description">
                          <textarea rows={3} className={`${blur.errCls("description", descFieldErr, inputCls)} resize-none`} disabled={ro} value={form.description} maxLength={500}
                            onChange={(e) => set("description", e.target.value)} onBlur={() => blur.touch("description")} />
                          {editing && (
                            <p className={`mt-1 text-xs ${blur.msg("description", descFieldErr) ? "text-red-500" : descLen > 500 ? "text-red-500" : "text-gray-400"}`}>
                              {blur.msg("description", descFieldErr) || `${descLen}/500`}
                            </p>
                          )}
                        </Field>
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard title={form.service_model === "national" ? "Serviceability" : "Location"}>
                    <div className="space-y-4">
                      <Field label="Lab type">
                        <SegmentedControl
                          disabled={ro}
                          value={form.service_model}
                          onChange={(v) => set("service_model", v)}
                          options={[
                            { value: "local", label: "Local Lab" },
                            { value: "national", label: "National Lab" },
                          ]}
                        />
                        {form.service_model !== (lab?.service_model || "local") && (
                          <p className="mt-1.5 inline-flex items-start gap-1.5 text-xs text-amber-600">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            {form.service_model === "national"
                              ? "Switching to National on save clears this lab's address — it will serve by uploaded pincodes instead."
                              : "Switching to Local on save keeps prior pincode versions but stops using them — add an address for radius serviceability."}
                          </p>
                        )}
                      </Field>

                      {form.service_model === "national" ? (
                        <LabPincodePanel role={currentRole} labId={labId} readOnly={ro} onToast={showToast} />
                      ) : (
                      <>
                      <Field label="Address line">
                        <input className={inputCls} disabled={ro} value={form.address_line} onChange={(e) => set("address_line", e.target.value)} />
                      </Field>
                      <Field label="Landmark">
                        <input className={inputCls} disabled={ro} value={form.landmark} onChange={(e) => set("landmark", e.target.value)} />
                      </Field>
                      <PincodeAutoAddress
                        disabled={ro}
                        blur={editing ? blur : null}
                        pincode={form.pincode}
                        locality={form.locality}
                        city={form.city}
                        state={form.state}
                        onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                      />
                      <div className="rounded-xl border border-gray-200 p-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                            <MapPin className="w-4 h-4" />
                          </span>
                          <div className="min-w-0">
                            {form.latitude && form.longitude ? (
                              <p className="text-sm font-medium text-gray-900 truncate">{form.latitude}, {form.longitude}</p>
                            ) : (
                              <>
                                <p className="text-sm font-medium text-gray-700">Coordinates not set</p>
                                <p className="text-[11px] text-gray-400">Auto-fetch from the address above</p>
                              </>
                            )}
                          </div>
                        </div>
                        {!ro && (
                          <button onClick={handleGeocode} disabled={geoBusy}
                            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors">
                            {geoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                            {form.latitude ? "Re-fetch lat/long" : "Auto-fetch lat/long"}
                          </button>
                        )}
                      </div>
                      {(editing || (form.latitude && form.longitude)) && (
                        <LocationPicker
                          disabled={ro}
                          latitude={form.latitude}
                          longitude={form.longitude}
                          onChange={({ latitude, longitude }) => setForm((f) => ({ ...f, latitude, longitude }))}
                        />
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Latitude">
                          <input className={blur.errCls("latitude", latFieldErr, inputCls)} disabled={ro} value={form.latitude} inputMode="decimal" placeholder="e.g. 28.6999318"
                            onChange={(e) => set("latitude", e.target.value.replace(/[^0-9.-]/g, ""))}
                            onBlur={() => blur.touch("latitude")} />
                          {blur.msg("latitude", latFieldErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("latitude", latFieldErr)}</p>}
                        </Field>
                        <Field label="Longitude">
                          <input className={blur.errCls("longitude", lngFieldErr, inputCls)} disabled={ro} value={form.longitude} inputMode="decimal" placeholder="e.g. 77.3329804"
                            onChange={(e) => set("longitude", e.target.value.replace(/[^0-9.-]/g, ""))}
                            onBlur={() => blur.touch("longitude")} />
                          {blur.msg("longitude", lngFieldErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("longitude", lngFieldErr)}</p>}
                        </Field>
                      </div>
                      </>
                      )}
                    </div>
                  </SectionCard>
                </div>

                <div className="space-y-6">
                  <SectionCard title="Chain" allowOverflow>
                    <AsyncChainSelect
                      role={currentRole}
                      disabled={ro}
                      value={form.chain}
                      label={form.chain_label}
                      onChange={(id, lbl) => setForm((f) => ({ ...f, chain: id, chain_label: lbl }))}
                    />
                    {chainManaged && !ro && (
                      <p className="mt-2 text-xs text-amber-700">
                        Changing the chain removes this lab from “{chainManaged.test_list?.name}” — it will revert to its own direct test list.
                      </p>
                    )}
                  </SectionCard>
                  <SectionCard title="Active">
                    <label className={`flex items-center gap-3 ${ro ? "" : "cursor-pointer"}`}>
                      <button type="button" role="switch" aria-checked={form.active} disabled={ro}
                        onClick={() => !ro && set("active", !form.active)}
                        className="relative inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-full disabled:cursor-default">
                        <span className={`block w-10 h-5 rounded-full transition-colors ${form.active ? "bg-emerald-500" : "bg-gray-300"}`} />
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.active ? "translate-x-5" : "translate-x-0"}`} />
                      </button>
                      <span className="text-sm text-gray-700">{form.active ? "Active" : "Inactive"}</span>
                    </label>
                  </SectionCard>
                  <SectionCard title="Record">
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between"><dt className="text-gray-400">Lab ID</dt><dd className="text-gray-600 font-mono text-xs truncate max-w-[140px]">{lab?.id}</dd></div>
                      <div className="flex justify-between"><dt className="text-gray-400">City</dt><dd className="text-gray-600">{lab?.city || "—"}</dd></div>
                      <AuditRow label="Created" at={lab?.created_at} by={lab?.created_by} />
                      <AuditRow label="Updated" at={lab?.updated_at} by={lab?.last_updated_by} />
                    </dl>
                  </SectionCard>
                  <SectionCard title="Samples served">
                    <Field label="Tests / samples served">
                      <input type="number" className={inputCls} disabled value={form.total_booking} placeholder="—" />
                    </Field>
                  </SectionCard>
                </div>
              </motion.div>
            )}

            {activeTab === "contacts" && (
              <motion.div key="contacts" {...panelMotion} className="space-y-6">
                <SectionCard title="Contacts">
                  {(form.contacts?.length || 0) === 0 ? (
                    ro ? (
                      <p className="py-2 text-sm text-gray-400">No contacts added.</p>
                    ) : (
                      <button onClick={addContact}
                        className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-blue-600 border border-dashed border-blue-200 rounded-xl hover:bg-blue-50 transition-all">
                        <Plus className="w-4 h-4" /> Add a contact
                      </button>
                    )
                  ) : ro ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {form.contacts.map((c, i) => {
                        const initials = (c.name || "").trim().split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
                        const hasDetails = c.mobile || c.whatsapp || c.email || c.pan;
                        return (
                          <div key={i}
                            className="group relative rounded-xl border border-[#E4EBF3] bg-white p-4 hover:border-[#0F4C81]/30 hover:shadow-[0_8px_22px_-10px_rgba(15,76,129,0.22)] transition-all duration-300">
                            {c.is_primary && (
                              <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[11px] font-medium ring-1 ring-inset ring-amber-200">
                                <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Primary
                              </span>
                            )}
                            <div className="flex items-center gap-3">
                              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#00A6A6] text-white flex items-center justify-center text-sm font-semibold shrink-0 shadow-sm ring-2 ring-white">
                                {initials}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">{c.name || "Unnamed contact"}</p>
                                {c.type && (
                                  <span className="inline-flex mt-1 px-1.5 py-0.5 rounded-md bg-[#0F4C81]/[0.06] text-[#0F4C81] text-[11px] font-medium">
                                    {labelFor(CONTACT_TYPE_OPTIONS, c.type)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 text-xs">
                              {c.mobile && (
                                <a href={`tel:${c.mobile}`} className="flex items-center gap-2 text-gray-600 hover:text-[#0F4C81] transition-colors">
                                  <span className="w-6 h-6 rounded-lg bg-gray-50 flex items-center justify-center shrink-0"><Phone className="w-3 h-3 text-gray-400" /></span>
                                  {c.mobile}
                                </a>
                              )}
                              {c.whatsapp && (
                                <a href={`https://wa.me/91${c.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors">
                                  <span className="w-6 h-6 rounded-lg bg-gray-50 flex items-center justify-center shrink-0"><MessageCircle className="w-3 h-3 text-gray-400" /></span>
                                  {c.whatsapp}
                                </a>
                              )}
                              {c.email && (
                                <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-gray-600 hover:text-[#0F4C81] transition-colors">
                                  <span className="w-6 h-6 rounded-lg bg-gray-50 flex items-center justify-center shrink-0"><Mail className="w-3 h-3 text-gray-400" /></span>
                                  <span className="truncate">{c.email}</span>
                                </a>
                              )}
                              {c.pan && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <span className="w-6 h-6 rounded-lg bg-gray-50 flex items-center justify-center shrink-0"><CreditCard className="w-3 h-3 text-gray-400" /></span>
                                  <span className="uppercase tracking-wide">{c.pan}</span>
                                </div>
                              )}
                              {!hasDetails && <p className="text-gray-400">No contact details</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {form.contacts.map((c, i) => {
                        const ce = contactFieldErrors(i, c);
                        return (
                        <div key={i} className="relative rounded-xl border border-gray-200 p-4 pt-3">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-semibold text-gray-400">Contact {i + 1}</span>
                            <button onClick={() => removeContact(i)} aria-label="Remove contact"
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Name" required>
                              <input className={blur.errCls(ck(i, "name"), ce[ck(i, "name")], inputCls)} value={c.name}
                                onChange={(e) => updateContact(i, "name", e.target.value)}
                                onBlur={() => blur.touch(ck(i, "name"))} />
                              {blur.msg(ck(i, "name"), ce[ck(i, "name")]) && <p className="mt-1 text-xs text-red-500">{blur.msg(ck(i, "name"), ce[ck(i, "name")])}</p>}
                            </Field>
                            <Field label="Type">
                              <select className={inputCls} value={c.type} onChange={(e) => updateContact(i, "type", e.target.value)}>
                                <option value="">Select type</option>
                                {CONTACT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </Field>
                            <Field label="Mobile" required>
                              <input className={blur.errCls(ck(i, "mobile"), ce[ck(i, "mobile")], inputCls)} value={c.mobile} maxLength={10} inputMode="numeric"
                                onChange={(e) => updateContact(i, "mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                                onBlur={() => blur.touch(ck(i, "mobile"))} />
                              {blur.msg(ck(i, "mobile"), ce[ck(i, "mobile")]) && <p className="mt-1 text-xs text-red-500">{blur.msg(ck(i, "mobile"), ce[ck(i, "mobile")])}</p>}
                            </Field>
                            <Field label="Email">
                              <input type="email" className={blur.errCls(ck(i, "email"), ce[ck(i, "email")], inputCls)} value={c.email}
                                onChange={(e) => updateContact(i, "email", e.target.value)}
                                onBlur={() => blur.touch(ck(i, "email"))} />
                              {blur.msg(ck(i, "email"), ce[ck(i, "email")]) && <p className="mt-1 text-xs text-red-500">{blur.msg(ck(i, "email"), ce[ck(i, "email")])}</p>}
                            </Field>
                            {CONTACT_TYPES_WITH_WHATSAPP.includes(c.type) && (
                              <Field label="WhatsApp number">
                                <input className={blur.errCls(ck(i, "whatsapp"), ce[ck(i, "whatsapp")], inputCls)} value={c.whatsapp} maxLength={10} inputMode="numeric"
                                  onChange={(e) => updateContact(i, "whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))}
                                  onBlur={() => blur.touch(ck(i, "whatsapp"))} />
                                {blur.msg(ck(i, "whatsapp"), ce[ck(i, "whatsapp")]) && <p className="mt-1 text-xs text-red-500">{blur.msg(ck(i, "whatsapp"), ce[ck(i, "whatsapp")])}</p>}
                              </Field>
                            )}
                            {CONTACT_TYPES_WITH_PAN.includes(c.type) && (
                              <Field label="PAN">
                                <input className={blur.errCls(ck(i, "pan"), ce[ck(i, "pan")], `${inputCls} uppercase`)} value={c.pan} maxLength={10}
                                  onChange={(e) => updateContact(i, "pan", e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 10))}
                                  onBlur={() => blur.touch(ck(i, "pan"))} />
                                {blur.msg(ck(i, "pan"), ce[ck(i, "pan")]) && <p className="mt-1 text-xs text-red-500">{blur.msg(ck(i, "pan"), ce[ck(i, "pan")])}</p>}
                              </Field>
                            )}
                          </div>
                          <label className="mt-3 inline-flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={c.is_primary} onChange={(e) => updateContact(i, "is_primary", e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-[#00A6A6]/60" />
                            <span className="text-sm text-gray-600">Primary contact</span>
                          </label>
                        </div>
                      ); })}
                      <button onClick={addContact}
                        className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-blue-600 border border-dashed border-blue-200 rounded-xl hover:bg-blue-50 transition-all">
                        <Plus className="w-4 h-4" /> Add another contact
                      </button>
                    </div>
                  )}
                </SectionCard>
              </motion.div>
            )}

            {activeTab === "media" && (
              <motion.div key="media" {...panelMotion} className="space-y-6">
                <SectionCard title="Photos & Gallery">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {LAB_PHOTO_SLOTS.map((slot) => {
                      const url = photos[slot.key];
                      const busy = photoBusy === slot.key;
                      return (
                        <div key={slot.key} className="space-y-1.5">
                          {ro ? (
                            <div className="relative aspect-video rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center">
                              {url ? (
                                <img src={url} alt={slot.label} className="w-full h-full object-cover" />
                              ) : (
                                <div className="flex flex-col items-center gap-1 text-gray-300">
                                  <ImageOff className="w-5 h-5" /><span className="text-[11px]">Not uploaded</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <label className={`relative aspect-video rounded-xl border border-dashed ${url ? "border-gray-200" : "border-blue-200"} bg-gray-50 overflow-hidden flex items-center justify-center cursor-pointer hover:bg-blue-50/40 transition-colors group`}>
                              <input type="file" accept="image/png,image/jpeg" className="hidden" disabled={busy}
                                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handlePhotoUpload(slot.key, f); }} />
                              {url && <img src={url} alt={slot.label} className="w-full h-full object-cover" />}
                              {busy ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
                              ) : (
                                <div className={`absolute inset-0 flex flex-col items-center justify-center gap-1 transition-all ${url ? "bg-black/0 group-hover:bg-black/40 text-transparent group-hover:text-white" : "text-blue-500"}`}>
                                  <ImagePlus className="w-5 h-5" /><span className="text-[11px] font-medium">{url ? "Replace" : "Upload"}</span>
                                </div>
                              )}
                            </label>
                          )}
                          <div>
                            <p className="text-xs font-medium text-gray-700">{slot.label}</p>
                            <p className="text-[11px] text-gray-400">{slot.hint}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 pt-5 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Additional gallery</p>
                        <p className="text-[11px] text-gray-400">PNG / JPEG · ≤2MB each</p>
                      </div>
                      <span className="text-xs text-gray-400">{gallery.length}/{LAB_GALLERY_MAX}</span>
                    </div>
                    {gallery.length === 0 && ro ? (
                      <p className="py-2 text-sm text-gray-400">No gallery images.</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {gallery.map((g) => (
                          <div key={g.id} className="relative aspect-square rounded-xl border border-gray-200 overflow-hidden group">
                            <img src={g.url} alt="Gallery" className="w-full h-full object-cover" />
                            {!ro && (
                              <button onClick={() => handlePhotoDelete(g.id)} disabled={photoBusy === g.id} aria-label="Remove photo"
                                className="absolute top-1 right-1 p-1 rounded-lg bg-white/90 text-gray-500 hover:text-red-600 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100">
                                {photoBusy === g.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                              </button>
                            )}
                          </div>
                        ))}
                        {!ro && gallery.length < LAB_GALLERY_MAX && (
                          <label className="aspect-square rounded-xl border border-dashed border-blue-200 bg-gray-50 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-blue-50/40 text-blue-500 transition-colors">
                            <input type="file" accept="image/png,image/jpeg" className="hidden" disabled={photoBusy === "gallery"}
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handlePhotoUpload("gallery", f, "gallery"); }} />
                            {photoBusy === "gallery" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                            <span className="text-[11px] font-medium">Add</span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                </SectionCard>
              </motion.div>
            )}

            {activeTab === "documents" && (
              <motion.div key="documents" {...panelMotion} className="space-y-6">
                <SectionCard title="Compliance & Documents">
                  <div className="space-y-4">
                    {LAB_DOC_TYPES.map((dt) => {
                      const val = documents[dt.key];
                      const files = dt.multi ? (val || []) : (val ? [val] : []);
                      const busy = docBusy === dt.key;
                      const canAddMore = dt.multi ? files.length < LAB_DOC_KYC_MAX : files.length === 0;
                      return (
                        <div key={dt.key} className="rounded-xl border border-gray-200 p-4">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-gray-800">{dt.label}</p>
                                {dt.required && <span className="px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[11px] font-medium">Required</span>}
                                {files.length === 0 && <span className="px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-400 text-[11px]">Not uploaded</span>}
                              </div>
                              {dt.hint && <p className="text-[11px] text-gray-400 mt-0.5">{dt.hint}</p>}
                            </div>
                            {!ro && canAddMore && (
                              <label className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors">
                                <input type="file" accept={LAB_DOC_ACCEPT} className="hidden" disabled={busy}
                                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleDocUpload(dt.key, f); }} />
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
                                {files.length ? (dt.multi ? "Add file" : "Replace") : "Upload"}
                              </label>
                            )}
                          </div>
                          {files.length > 0 && (
                            <ul className="space-y-1.5">
                              {files.map((f) => (
                                <li key={f.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                                  <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                                  <a href={f.url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline truncate flex-1">{f.filename || "Document"}</a>
                                  {!ro && (
                                    <button onClick={() => handleDocDelete(f.id)} disabled={docBusy === f.id} aria-label="Remove document"
                                      className="p-1 text-gray-400 hover:text-red-600 rounded">
                                      {docBusy === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                    </button>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>
              </motion.div>
            )}

            {activeTab === "bank" && (
              <motion.div key="bank" {...panelMotion} className="space-y-6">
                <SectionCard title="Bank Details">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Account holder name">
                      <input className={inputCls} disabled={ro} value={form.bank.account_name} onChange={(e) => setBank("account_name", e.target.value)} />
                    </Field>
                    <Field label="Account number">
                      <input className={inputCls} disabled={ro} value={form.bank.account_number} maxLength={20}
                        onChange={(e) => setBank("account_number", e.target.value.replace(/\D/g, ""))} />
                    </Field>
                    <Field label="IFSC code">
                      <input className={blur.errCls("ifsc_code", ifscErr, `${inputCls} uppercase`)} disabled={ro} value={form.bank.ifsc_code} maxLength={11}
                        onChange={(e) => setBank("ifsc_code", e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 11))}
                        onBlur={() => blur.touch("ifsc_code")} />
                      {blur.msg("ifsc_code", ifscErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("ifsc_code", ifscErr)}</p>}
                    </Field>
                    <Field label="Bank name">
                      <input className={inputCls} disabled={ro} value={form.bank.bank_name} onChange={(e) => setBank("bank_name", e.target.value)} />
                    </Field>
                    <Field label="Branch name">
                      <input className={inputCls} disabled={ro} value={form.bank.branch_name} onChange={(e) => setBank("branch_name", e.target.value)} />
                    </Field>
                    <Field label="Account type">
                      <select className={inputCls} disabled={ro} value={form.bank.account_type} onChange={(e) => setBank("account_type", e.target.value)}>
                        <option value="">Select type</option>
                        <option value="s">Savings</option>
                        <option value="c">Current</option>
                      </select>
                    </Field>
                  </div>
                </SectionCard>
              </motion.div>
            )}

            {activeTab === "tests" && (
              <motion.div key="tests" {...panelMotion} className="space-y-6">
                <SectionCard title="Tests & Packages">
                  <div className="space-y-4">
                    {liveTestCount > 0 && (
                      <div className="pb-4 border-b border-gray-100">
                        <button onClick={() => setTestsDrawerOpen(true)}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/40 transition-all">
                          <span className="flex items-center gap-2.5 min-w-0">
                            <span className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center shrink-0"><FlaskConical className="w-4 h-4 text-blue-600" /></span>
                            <span className="text-left min-w-0">
                              <span className="block text-sm font-semibold text-gray-900">{liveTestCount} tests &amp; packages</span>
                              <span className="block text-[11px] text-gray-400">Search, filter &amp; view pricing</span>
                            </span>
                          </span>
                          <span className="text-xs font-medium text-blue-600 inline-flex items-center gap-1 shrink-0">View <ChevronRight className="w-4 h-4" /></span>
                        </button>
                      </div>
                    )}
                    {chainManaged && (
                      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-[#E6F6F6] border border-[#00A6A6]/20">
                        <FlaskConical className="w-4 h-4 text-[#00A6A6] mt-0.5 shrink-0" />
                        <div className="min-w-0 text-sm">
                          <p className="font-medium text-gray-900">
                            This lab's test list is managed through {chainManaged.chain?.brand_name} · {chainManaged.test_list?.name}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5">To change its tests, update the list from the Diagnostic Chain.</p>
                          <button onClick={() => navigate(`/subscriber-wallet/labs/chains/${chainManaged.chain?.id}/test-lists`)}
                            className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-[#0F4C81] hover:underline">
                            Open chain test lists <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <button onClick={handleTemplateDownload}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                        <Download className="w-3.5 h-3.5" /> Download template
                      </button>
                      {!ro && !chainManaged && (
                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors">
                          <input type="file" accept={LAB_TEST_SHEET_ACCEPT} className="hidden" disabled={testBusy}
                            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pickTestFile(f); }} />
                          {testBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
                          {testSheet.current ? "Upload new version" : "Upload sheet"}
                        </label>
                      )}
                    </div>

                    {testSheet.current ? (
                      <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-gray-50 border border-gray-100">
                        <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                          <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <a href={testSheet.current.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 hover:underline truncate">{testSheet.current.filename || "Test sheet"}</a>
                            <span className="px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-600 text-[11px] font-medium">v{testSheet.current.version} · current</span>
                            {typeof testSheet.current.test_count === "number" && (
                              <span className="px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600 text-[11px] font-medium">{testSheet.current.test_count} tests</span>
                            )}
                          </div>
                          <p className="text-[11px] text-gray-400 mt-0.5">Uploaded {testSheet.current.uploaded_at ? new Date(testSheet.current.uploaded_at).toLocaleDateString() : "—"}</p>
                          {chainManaged && <p className="text-[11px] text-gray-500 mt-0.5">This lab's own upload — not in use while it uses the chain list.</p>}
                          {testSheet.current.error_file && (
                            <a href={testSheet.current.error_file} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 text-[11px] font-medium text-amber-700 hover:text-amber-900 hover:underline">
                              <AlertTriangle className="w-3 h-3 shrink-0" /> Some rows had errors — download report
                            </a>
                          )}
                        </div>
                        <a href={testSheet.current.url} target="_blank" rel="noreferrer" className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg" aria-label="Download current sheet"><Download className="w-4 h-4" /></a>
                      </div>
                    ) : (
                      <p className="py-2 text-sm text-gray-400">No test sheet uploaded yet.</p>
                    )}

                    {testSheet.versions.length > 1 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Version history</p>
                        <ul className="space-y-1.5">
                          {[...testSheet.versions].reverse().slice(0, 5).map((v) => (
                            <li key={v.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-100 text-sm">
                              <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <a href={v.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate flex-1">{v.filename || `Version ${v.version}`}</a>
                              <span className={`px-1.5 py-0.5 rounded-md text-[11px] font-medium ${v.is_current ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-400"}`}>v{v.version}{v.is_current ? " · current" : ""}</span>
                              <span className="text-[11px] text-gray-400 hidden sm:block">{v.uploaded_at ? new Date(v.uploaded_at).toLocaleDateString() : ""}</span>
                            </li>
                          ))}
                        </ul>
                        {testSheet.versions.length > 5 && (
                          <p className="mt-2 text-[11px] text-gray-400">Showing latest 5 of {testSheet.versions.length} versions.</p>
                        )}
                      </div>
                    )}

                    {testProcessing && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 text-blue-600 text-xs">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Parsing the uploaded sheet in the background…
                      </div>
                    )}

                  </div>
                </SectionCard>
              </motion.div>
            )}
          </>
        )}

        {/* Operations & schedule — compact summary; full editor lives in a drawer */}
        {!loading && form && activeTab === "operations" && (
          <motion.div key="operations" {...panelMotion} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" /> Operations &amp; schedule
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Collection modes, capacity, phlebotomist charges and weekly operating hours.</p>
              </div>
              {!opsLoading && operations && (
                <button onClick={() => setOpsDrawerOpen(true)}
                  className="px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center gap-2 shrink-0 transition-all">
                  <Pencil className="w-4 h-4" /> Edit
                </button>
              )}
            </div>
            <div className="px-6 py-5">
              {opsLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-6">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading operations…
                </div>
              ) : !opsSummary ? (
                <div className="text-center py-10">
                  <div className="mx-auto w-11 h-11 rounded-xl bg-gray-50 flex items-center justify-center mb-3">
                    <Clock className="w-5 h-5 text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">Operations not configured yet</p>
                  <p className="text-xs text-gray-400 mt-1 mb-4">Set collection modes, capacity and operating hours for this lab.</p>
                  <button onClick={() => setOpsDrawerOpen(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#0F4C81] rounded-xl hover:bg-[#0C3E6B] transition-colors">
                    <Plus className="w-4 h-4" /> Configure operations
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Collection modes */}
                  <div className="flex flex-wrap gap-2">
                    {opsSummary.home && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium ring-1 ring-inset ring-emerald-600/20">
                        <Home className="w-3.5 h-3.5" /> Home collection
                      </span>
                    )}
                    {opsSummary.walkin && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium ring-1 ring-inset ring-blue-600/20">
                        <Building2 className="w-3.5 h-3.5" /> Walk-in
                      </span>
                    )}
                    {opsSummary.femalePhlebo && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-pink-50 text-pink-700 text-xs font-medium ring-1 ring-inset ring-pink-600/20">
                        <UserRound className="w-3.5 h-3.5" /> Female phlebotomist
                      </span>
                    )}
                  </div>

                  {/* Key stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5">
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide">Report TAT</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">{opsSummary.reportTat ? `${opsSummary.reportTat} hrs` : "—"}</p>
                    </div>
                    <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5">
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide">Booking cutoff</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">{opsSummary.cutoff != null ? `${opsSummary.cutoff} min` : "—"}</p>
                    </div>
                    {opsSummary.home && (
                      <>
                        <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5">
                          <p className="text-[11px] text-gray-400 uppercase tracking-wide">Coverage</p>
                          <p className="text-sm font-semibold text-gray-900 mt-0.5">{opsSummary.coverage ? `${opsSummary.coverage} km` : "—"}</p>
                        </div>
                        <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5">
                          <p className="text-[11px] text-gray-400 uppercase tracking-wide">Phlebo charge</p>
                          <p className="text-sm font-semibold text-gray-900 mt-0.5">{inr(opsSummary.phleboCharge)}</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Operating hours */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide">Operating hours</p>
                      {opsSummary.lunch && (
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-600">
                          <Utensils className="w-3 h-3" /> Lunch {opsSummary.lunch}
                        </span>
                      )}
                    </div>
                    <div className="rounded-xl border border-[#E4EBF3] divide-y divide-gray-100 overflow-hidden">
                      {opsSummary.schedule.map((s) => (
                        <div key={s.key}
                          className={`flex items-center justify-between px-3 py-2 text-sm ${s.active ? "bg-white" : "bg-gray-50/60"}`}>
                          <span className={`font-medium ${s.active ? "text-gray-800" : "text-gray-400"}`}>{s.label}</span>
                          {s.active ? (
                            <span className="inline-flex items-center gap-2 text-gray-600 tabular-nums">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              {s.open} <span className="text-gray-300">–</span> {s.close}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Closed</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
      <Toast toast={toast} />
      <TestsDrawer
        open={testsDrawerOpen}
        onClose={() => setTestsDrawerOpen(false)}
        role={currentRole}
        labId={labId}
        labName={lab?.brand_name || "Lab"}
        onSaveTest={chainManaged ? undefined : (test, payload) => updateLabTest(currentRole, labId, test.id, payload)}
        readOnlyNote={chainManaged
          ? `These tests come from ${chainManaged.chain?.brand_name} · ${chainManaged.test_list?.name}. Edit them from the chain's test lists.`
          : undefined}
      />
      <OperationsModal
        open={opsDrawerOpen}
        onClose={() => setOpsDrawerOpen(false)}
        role={currentRole}
        labId={labId}
        labName={lab?.brand_name || "Lab"}
        operations={operations}
        onSaved={handleOpsSaved}
      />

      {pendingTestFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[1px] p-4"
          onMouseDown={(e) => e.target === e.currentTarget && setPendingTestFile(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 py-5">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">Replace the current test menu?</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This lab already has{" "}
                    <span className="font-medium text-gray-700">{testSheet.current?.test_count || 0} tests</span>{" "}
                    from the current sheet. Uploading <span className="font-medium text-gray-700">{pendingTestFile.name}</span> will
                    replace them with the new file's data. The previous version is kept for audit.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setPendingTestFile(null)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-xl hover:bg-white transition-colors">
                Cancel
              </button>
              <button onClick={confirmTestUpload}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-xl hover:bg-amber-700 transition-colors">
                Replace &amp; upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagnosticLabDetail;
